import styled from "@emotion/styled";
import { Box, Typography } from "@mui/material";
import React from "react";
import UpIcon from "@icons/positive.svg?react";
import DownIcon from "@icons/negative.svg?react";
import { toPercent } from "@helpers";
import { useGetGainLossStyles } from "./useGetGainLossStyles";

export interface GainLossPercentsProps {
  value: number;
  size?: "small" | "medium";
  className?: string;
  reversed?: boolean;
  showArrows?: boolean;
  showDashOnZero?: boolean;
  variant?: "colored" | "neutral";
}

export const GainLossPercentsTypography = styled(Typography, {
  shouldForwardProp: (prop) =>
    prop !== "value" &&
    prop !== "reversed" &&
    prop !== "padding" &&
    prop !== "neutral",
})<{
  size: "small" | "medium";
  padding: number[];
  value?: number;
  reversed?: boolean;
  neutral?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme, padding }) =>
    theme.spacing(...[padding[0], padding[1]])};
  border-radius: ${({ theme }) =>
    theme.type === "ariva_de" ? "100px" : `${theme.shape.borderRadius}px`};
  color: ${({
    theme: {
      palette: { text },
    },
    reversed,
    neutral,
    value = 0,
  }) => {
    if (neutral) {
      return text.secondary;
    }
    if (reversed) {
      return value > 0 ? text.negativeOnBadge : text.positiveOnBadge;
    }
    return value > 0 ? text.positiveOnBadge : text.negativeOnBadge;
  }};
  background: ${({
    theme: {
      palette: { background },
    },
    reversed,
    neutral,
    value = 0,
  }) => {
    if (neutral) {
      return background.neutral;
    }
    if (reversed) {
      return value > 0 ? background.error : background.success;
    }
    return value > 0 ? background.success : background.error;
  }};
`;

const formatPercent = (value?: number) => (value ? toPercent(value) : "");

export const GainLossPercents = ({
  value,
  className,
  reversed = false,
  showArrows = true,
  showDashOnZero = false,
  size = "small",
  variant = "colored",
}: GainLossPercentsProps) => {
  const componentStyles = useGetGainLossStyles();
  if (!value) {
    return showDashOnZero ? (
      <Typography variant="body4" color="textSecondary">
        -
      </Typography>
    ) : null;
  }

  const renderArrows = () => {
    if (!showArrows) return null;
    return value < 0 ? (
      <DownIcon width="10px" height="8px" />
    ) : (
      <UpIcon width="10px" height="8px" />
    );
  };

  return (
    <GainLossPercentsTypography
      padding={componentStyles.padding[size]}
      variant={componentStyles.fontVariant[size]}
      value={value}
      className={className}
      reversed={reversed}
      neutral={variant === "neutral"}
      size={size}
    >
      {value > 0 && "+"}
      <Box component="span" marginRight={1}>
        {formatPercent(value)}
      </Box>
      {renderArrows()}
    </GainLossPercentsTypography>
  );
};
